// src/components/wells/WellManagementPanel.js
import React, { useState, useMemo, memo, useCallback } from 'react';
// --- UPDATE
import { useEffect, useRef } from 'react';
// --- END UPDATE
import {
  Box,
  Flex,
  Text,
  Heading,
  Button,
  IconButton,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Image,
  VStack,
  HStack,
  Divider,
  useToast,
  Tooltip,
  Tag,
  TagLabel,
  TagLeftIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerFooter,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  // --- UPDATE
  Skeleton,
  SkeletonText,
  CircularProgress,
  CircularProgressLabel,
  Progress,
  Wrap,
  WrapItem,
  // --- END UPDATE
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdWaterDrop,
  MdPerson,
  MdAssignmentTurnedIn,
  MdShield,
  MdSensors,
  MdInfo,
  MdBrokenImage,
  MdCheckCircle,
  MdWarning,
  MdErrorOutline,
  MdCalendarToday,
  MdPhotoCamera,
  MdClose,
  MdZoomIn,
  MdBuild,
  MdAutoAwesome,
  // --- UPDATE
  MdWbSunny,
  MdWbCloudy,
  MdCloudQueue,
  MdGrain,
  MdFlashOn,
  MdOpacity,
  MdTimer,
  MdContentCopy,
  MdLightbulbOutline,
  MdUpdate,
  MdExpandMore,
  MdSpeed,
  MdFavorite,
  // --- END UPDATE
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

// 🚀 ENTERPRISE ADDITIONS: AI Prediction Components
import WellAiSummaryCard from './WellAiSummaryCard';
import WellAiStatusCell from './WellAiStatusCell';
// --- UPDATE (wells v1.3): drainage wells, catch basins, elevation
import WellDrainageNetworkTab from './WellDrainageNetworkTab';
import { isWellType, tierMeta } from './wellIntelUi';
// --- END UPDATE
// --- UPDATE: shared AI helpers
import {
  getAiForWell,
  extractAiMetrics,
  getAiRiskTier,
  AI_RISK_TIERS,
  getTrendMeta,
  getRainfallRisk,
  formatMinutesShort,
  formatRelativeTime,
  AiSparkline,
  STALE_PREDICTION_MS,
  usePrefersReducedMotion,
} from './WellAiStatusCell';
// --- END UPDATE

const MotionTr = motion(Tr);

export const resolvePhotoUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('http://159.203.120.106:3001')) {
    return trimmed.replace('http://159.203.120.106:3001', 'https://flowsense.live');
  }
  if (trimmed.startsWith('http://flowsense.live')) {
    return trimmed.replace('http://', 'https://');
  }
  if (trimmed.startsWith('/uploads') || trimmed.startsWith('uploads')) {
    const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ) {
      return `http://159.203.120.106:3001${cleanPath}`;
    }
    return cleanPath;
  }
  return trimmed;
};

const GlassPanel = ({ children, gradient = false, p = 5, ...props }) => {
  const glassBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(26, 32, 44, 0.85)');
  const glassBorder = useColorModeValue('rgba(255, 255, 255, 0.45)', 'rgba(255, 255, 255, 0.08)');
  const gradientBg = useColorModeValue(
    'linear-gradient(135deg, rgba(0, 212, 170, 0.04) 0%, rgba(49, 130, 206, 0.04) 100%)',
    'linear-gradient(135deg, rgba(0, 212, 170, 0.08) 0%, rgba(49, 130, 206, 0.08) 100%)'
  );

  return (
    <Box
      bg={gradient ? gradientBg : glassBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={glassBorder}
      borderRadius="2xl"
      p={p}
      position="relative"
      overflow="hidden"
      boxShadow="sm"
      transition="all 0.25s ease-in-out"
      {...props}
    >
      {children}
    </Box>
  );
};

const getLevelBadgeColor = (level) => {
  switch ((level || '').toUpperCase()) {
    case 'HIGH':
    case 'OPTIMAL':
      return 'green';
    case 'MEDIUM':
    case 'MODERATE':
      return 'blue';
    case 'LOW':
    case 'MINIMAL':
      return 'orange';
    case 'EMPTY':
    case 'CRITICAL':
      return 'red';
    default:
      return 'gray';
  }
};

const getCasingBadgeDetails = (condition) => {
  switch ((condition || '').toLowerCase()) {
    case 'critical':
      return { colorScheme: 'red', icon: MdErrorOutline, label: 'CRITICAL / COMPROMISED' };
    case 'degraded':
      return { colorScheme: 'orange', icon: MdWarning, label: 'DEGRADED / SCALING' };
    case 'good':
    default:
      return { colorScheme: 'green', icon: MdCheckCircle, label: 'SOUND / STABLE' };
  }
};

// --- UPDATE: live weather engine (Open-Meteo — free, keyless, CORS-enabled)
const DEFAULT_WEATHER_LOCATION = Object.freeze({ lat: 25.0443, lng: -77.3504, label: 'New Providence' });
const OPEN_METEO_URL = 'https://api.open-meteo.com/v1/forecast';
const WEATHER_REFRESH_MS_DEFAULT = 10 * 60 * 1000;
const DEFAULT_PAGE_SIZE = 50;
const ROW_ANIMATION_LIMIT = 40;
const weatherCache = new Map(); // key -> { data, ts }
const weatherInflight = new Map(); // key -> Promise (dedupes fleet + drawer requests)

const WEATHER_CODE_META = [
  { max: 0, label: 'Clear', icon: MdWbSunny },
  { max: 3, label: 'Partly cloudy', icon: MdWbCloudy },
  { max: 48, label: 'Fog', icon: MdCloudQueue },
  { max: 57, label: 'Drizzle', icon: MdGrain },
  { max: 67, label: 'Rain', icon: MdGrain },
  { max: 77, label: 'Snow', icon: MdCloudQueue },
  { max: 82, label: 'Rain showers', icon: MdGrain },
  { max: 86, label: 'Snow showers', icon: MdCloudQueue },
  { max: 99, label: 'Thunderstorm', icon: MdFlashOn },
];

const getWeatherCodeMeta = (code) => {
  const n = Number(code);
  if (code === null || code === undefined || !Number.isFinite(n)) return { label: '—', icon: MdCloudQueue };
  return WEATHER_CODE_META.find((m) => n <= m.max) || WEATHER_CODE_META[WEATHER_CODE_META.length - 1];
};

const round1 = (n) => Math.round(n * 10) / 10;
const numOrNull = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));

const parseCoordPair = (latRaw, lngRaw) => {
  const lat = parseFloat(latRaw);
  const lng = parseFloat(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
};

/** Normalises the Open-Meteo payload into a compact, UI-ready shape. */
const normalizeOpenMeteo = (json, nowMs = Date.now()) => {
  const offsetMs = (Number(json?.utc_offset_seconds) || 0) * 1000;
  const toEpoch = (t) => Date.parse(`${t}Z`) - offsetMs;
  const times = json?.hourly?.time || [];
  const precip = json?.hourly?.precipitation || [];
  const probs = json?.hourly?.precipitation_probability || [];

  let startIdx = times.findIndex((t) => toEpoch(t) + 3600000 > nowMs);
  if (startIdx < 0) startIdx = times.length;

  const sumRange = (from, to) => {
    let s = 0;
    for (let i = Math.max(0, from); i < Math.min(precip.length, to); i += 1) s += Number(precip[i]) || 0;
    return round1(s);
  };

  const hourly = [];
  for (let i = startIdx; i < Math.min(times.length, startIdx + 24); i += 1) {
    hourly.push({
      time: toEpoch(times[i]),
      precipMm: Number(precip[i]) || 0,
      prob: numOrNull(probs[i]),
    });
  }

  let peakPrecipProbability = null;
  let peakTime = null;
  hourly.forEach((h) => {
    if (h.prob !== null && (peakPrecipProbability === null || h.prob > peakPrecipProbability)) {
      peakPrecipProbability = h.prob;
      peakTime = h.time;
    }
  });

  const today = String(json?.current?.time || '').slice(0, 10);
  const daily = (json?.daily?.time || [])
    .map((d, i) => ({
      date: d,
      rainMm: round1(Number(json.daily.precipitation_sum?.[i]) || 0),
      prob: numOrNull(json.daily.precipitation_probability_max?.[i]),
    }))
    .filter((d) => !today || d.date >= today)
    .slice(0, 3);

  const weatherCode = numOrNull(json?.current?.weather_code);
  return {
    source: 'Open-Meteo',
    fetchedAt: nowMs,
    current: {
      temperatureC: numOrNull(json?.current?.temperature_2m),
      precipitationMm: numOrNull(json?.current?.precipitation),
      windKph: numOrNull(json?.current?.wind_speed_10m),
      weatherCode,
      label: getWeatherCodeMeta(weatherCode).label,
    },
    next6hRainMm: sumRange(startIdx, startIdx + 6),
    next24hRainMm: sumRange(startIdx, startIdx + 24),
    past24hRainMm: sumRange(startIdx - 24, startIdx),
    peakPrecipProbability,
    peakTime,
    hourly,
    daily,
  };
};

const weatherKey = (lat, lng) => `${lat.toFixed(2)},${lng.toFixed(2)}`;

const fetchOpenMeteoWeather = (lat, lng) => {
  const key = weatherKey(lat, lng);
  if (weatherInflight.has(key)) return weatherInflight.get(key);
  const params = new URLSearchParams({
    latitude: lat.toFixed(4),
    longitude: lng.toFixed(4),
    current: 'temperature_2m,precipitation,weather_code,wind_speed_10m',
    hourly: 'precipitation,precipitation_probability',
    daily: 'precipitation_sum,precipitation_probability_max',
    past_days: '1',
    forecast_days: '3',
    timezone: 'auto',
  });
  const request = fetch(`${OPEN_METEO_URL}?${params.toString()}`)
    .then((res) => {
      if (!res.ok) throw new Error(`Weather service responded ${res.status}`);
      return res.json();
    })
    .then((json) => {
      const data = normalizeOpenMeteo(json);
      weatherCache.set(key, { data, ts: Date.now() });
      return data;
    })
    .finally(() => weatherInflight.delete(key));
  weatherInflight.set(key, request);
  return request;
};

/** Cached, deduplicated, visibility-aware weather polling. */
const useLiveWeather = (lat, lng, { enabled = true, refreshMs = WEATHER_REFRESH_MS_DEFAULT } = {}) => {
  const [state, setState] = useState({ data: null, isLoading: false, error: null });
  const [reloadToken, setReloadToken] = useState(0);
  const forceRef = useRef(false);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  useEffect(() => {
    if (!enabled || !hasCoords) return undefined;
    let cancelled = false;
    const key = weatherKey(lat, lng);
    const ttl = Math.max(60000, Number(refreshMs) || WEATHER_REFRESH_MS_DEFAULT);

    const load = (force) => {
      const cached = weatherCache.get(key);
      if (!force && cached && Date.now() - cached.ts < ttl) {
        setState({ data: cached.data, isLoading: false, error: null });
        return;
      }
      setState((s) => ({ data: s.data ?? cached?.data ?? null, isLoading: true, error: null }));
      fetchOpenMeteoWeather(lat, lng)
        .then((data) => {
          if (!cancelled) setState({ data, isLoading: false, error: null });
        })
        .catch((error) => {
          if (!cancelled) setState((s) => ({ data: s.data, isLoading: false, error }));
        });
    };

    load(forceRef.current);
    forceRef.current = false;

    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') load(true);
    }, ttl);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, hasCoords, lat, lng, refreshMs, reloadToken]);

  const reload = useCallback(() => {
    forceRef.current = true;
    setReloadToken((t) => t + 1);
  }, []);

  return { ...state, reload };
};

/** 24-hour rainfall bars (mm) with precipitation-probability shading behind each bar. */
const HourlyRainBars = memo(({ hourly = [], height = 36, subtextColor, emptyBarColor = 'gray.300' }) => {
  const maxMm = useMemo(() => hourly.reduce((m, h) => Math.max(m, h.precipMm), 1), [hourly]);
  if (!hourly.length) return null;
  return (
    <Box>
      <Flex align="flex-end" h={`${height}px`} gap="2px" role="img" aria-label="Hourly rainfall forecast, next 24 hours">
        {hourly.map((h) => (
          <Tooltip
            key={h.time}
            hasArrow
            openDelay={120}
            label={`${new Date(h.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · ${h.precipMm.toFixed(1)} mm${
              h.prob !== null ? ` · ${h.prob}% chance` : ''
            }`}
          >
            <Box flex="1" position="relative" h="100%" display="flex" alignItems="flex-end">
              {h.prob !== null && (
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  h={`${h.prob}%`}
                  bg="purple.300"
                  opacity={0.18}
                  borderRadius="1px"
                />
              )}
              <Box
                w="100%"
                h={`${Math.max(h.precipMm > 0 ? 8 : 3, (h.precipMm / maxMm) * 100)}%`}
                bg={h.precipMm > 0 ? 'blue.400' : emptyBarColor}
                opacity={h.precipMm > 0 ? 0.9 : 0.4}
                borderRadius="1px"
                position="relative"
              />
            </Box>
          </Tooltip>
        ))}
      </Flex>
      <Flex justify="space-between" mt={1}>
        <Text fontSize="9px" color={subtextColor}>Now</Text>
        <Text fontSize="9px" color={subtextColor}>+12h</Text>
        <Text fontSize="9px" color={subtextColor}>+24h</Text>
      </Flex>
    </Box>
  );
});
HourlyRainBars.displayName = 'HourlyRainBars';

const WeatherStat = ({ label, value, unit, subtextColor, color }) => (
  <Box minW="62px">
    <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color={subtextColor} letterSpacing="0.04em">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="800" color={color}>
      {value ?? '—'}
      {value !== null && value !== undefined && unit ? (
        <Text as="span" fontSize="2xs" fontWeight="600" ml={0.5}>
          {unit}
        </Text>
      ) : null}
    </Text>
  </Box>
);

/** Fleet-level weather + AI coupling strip shown under the KPI grid. */
const FleetWeatherStrip = memo(
  ({ weather, isLoading, error, onReload, locationLabel, insight, subtextColor, cardBorderColor, emptyBarColor }) => {
    const rainRisk = getRainfallRisk(weather?.next24hRainMm);
    const codeMeta = getWeatherCodeMeta(weather?.current?.weatherCode);

    if (!weather && isLoading) {
      return (
        <GlassPanel p={4} gradient>
          <HStack spacing={4}>
            <Skeleton boxSize="36px" borderRadius="full" />
            <Box flex="1">
              <SkeletonText noOfLines={2} spacing={2} skeletonHeight="2" />
            </Box>
            <Skeleton h="36px" w="220px" borderRadius="md" display={{ base: 'none', md: 'block' }} />
          </HStack>
        </GlassPanel>
      );
    }

    if (!weather && error) {
      return (
        <GlassPanel p={3} gradient>
          <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
            <HStack spacing={2}>
              <Icon as={MdCloudQueue} color="gray.400" boxSize={5} />
              <Box>
                <Text fontSize="xs" fontWeight="700">Live weather feed unavailable</Text>
                <Text fontSize="2xs" color={subtextColor}>
                  AI flood predictions continue without rainfall coupling. {error.message}
                </Text>
              </Box>
            </HStack>
            <Button size="xs" variant="outline" leftIcon={<MdRefresh />} onClick={onReload}>
              Retry
            </Button>
          </Flex>
        </GlassPanel>
      );
    }

    if (!weather) return null;

    return (
      <GlassPanel p={4} gradient>
        <Flex gap={5} align="center" flexWrap="wrap">
          <HStack spacing={3} minW="170px">
            <Flex
              boxSize="42px"
              borderRadius="full"
              align="center"
              justify="center"
              bg="blackAlpha.50"
              borderWidth="1px"
              borderColor={cardBorderColor}
            >
              <Icon as={codeMeta.icon} boxSize={6} color={codeMeta.icon === MdWbSunny ? 'yellow.400' : 'blue.400'} />
            </Flex>
            <Box>
              <Text fontSize="9px" fontWeight="800" textTransform="uppercase" color={subtextColor} letterSpacing="0.06em">
                Live Weather · {locationLabel}
              </Text>
              <HStack spacing={2} align="baseline">
                <Text fontSize="xl" fontWeight="900">
                  {weather.current.temperatureC !== null ? `${Math.round(weather.current.temperatureC)}°C` : '—'}
                </Text>
                <Text fontSize="xs" fontWeight="600" color={subtextColor}>
                  {weather.current.label}
                  {weather.current.windKph !== null ? ` · ${Math.round(weather.current.windKph)} km/h` : ''}
                </Text>
              </HStack>
            </Box>
          </HStack>

          <HStack spacing={4} flexWrap="wrap">
            <WeatherStat label="Next 6h" value={weather.next6hRainMm} unit="mm" subtextColor={subtextColor} color="blue.400" />
            <WeatherStat label="Next 24h" value={weather.next24hRainMm} unit="mm" subtextColor={subtextColor} color="blue.500" />
            <WeatherStat label="Past 24h" value={weather.past24hRainMm} unit="mm" subtextColor={subtextColor} />
            <WeatherStat
              label="Peak chance"
              value={weather.peakPrecipProbability}
              unit="%"
              subtextColor={subtextColor}
              color="purple.400"
            />
          </HStack>

          <Box flex="1" minW={{ base: '100%', md: '220px' }}>
            <HourlyRainBars hourly={weather.hourly} subtextColor={subtextColor} emptyBarColor={emptyBarColor} />
          </Box>

          <VStack align={{ base: 'start', lg: 'end' }} spacing={1} minW={{ base: '100%', lg: '220px' }} maxW={{ lg: '280px' }}>
            <HStack spacing={2}>
              <Badge colorScheme={rainRisk.scheme} borderRadius="full" px={2} fontSize="9px">
                RAINFALL RISK: {rainRisk.level}
              </Badge>
              <Tooltip hasArrow label={`Source: ${weather.source} · updated ${formatRelativeTime(weather.fetchedAt) || ''}`}>
                <IconButton
                  size="xs"
                  variant="ghost"
                  icon={<MdRefresh />}
                  isLoading={isLoading}
                  onClick={onReload}
                  aria-label="Refresh weather"
                />
              </Tooltip>
            </HStack>
            {insight && (
              <HStack align="start" spacing={1}>
                <Icon as={MdAutoAwesome} color="purple.400" boxSize={3} mt="2px" flexShrink={0} />
                <Text fontSize="2xs" fontWeight="600" textAlign={{ base: 'left', lg: 'right' }}>
                  {insight}
                </Text>
              </HStack>
            )}
          </VStack>
        </Flex>
      </GlassPanel>
    );
  }
);
FleetWeatherStrip.displayName = 'FleetWeatherStrip';

/** Rule-based actions used only when the model returns no recommendations of its own. */
const deriveSuggestedActions = (metrics, tierKey, clearanceScore, siteWeather) => {
  const actions = [];
  if (!metrics) return actions;
  if (metrics.estMinutes !== null && metrics.estMinutes <= 60) {
    actions.push('Dispatch a field crew — overtopping is projected within the hour.');
  }
  if (tierKey === 'critical') {
    actions.push('Notify roads & drainage teams of potential corridor inundation.');
  }
  if (Number.isFinite(clearanceScore) && clearanceScore < 80) {
    actions.push(`Schedule desilting — wellbore clearance at ${clearanceScore}% reduces storage capacity.`);
  }
  if (siteWeather && siteWeather.next24hRainMm >= 25) {
    actions.push(`Pre-position pumps ahead of ${siteWeather.next24hRainMm} mm forecast rainfall.`);
  }
  if (metrics.healthScore < 50) {
    actions.push('Plan asset rehabilitation — health score is below 50%.');
  }
  if (!actions.length) actions.push('No intervention required — continue routine telemetry monitoring.');
  return actions;
};

/** Full AI + weather analysis tab inside the details drawer. */
const WellAiInsightsPanel = memo(
  ({
    well,
    ai,
    clearanceScore,
    siteWeather,
    weatherLoading,
    weatherError,
    onReloadWeather,
    onCopySummary,
    subtextColor,
    cardBorderColor,
    glassCardBg,
    emptyBarColor,
    staleAfterMs,
  }) => {
    const metrics = useMemo(() => extractAiMetrics(ai), [ai]);
    const tierKey = getAiRiskTier(metrics) || 'stable';
    const tier = AI_RISK_TIERS[tierKey];
    const trendMeta = getTrendMeta(metrics?.trend);
    const actions = useMemo(
      () =>
        metrics?.recommendations?.length
          ? metrics.recommendations
          : deriveSuggestedActions(metrics, tierKey, clearanceScore, siteWeather),
      [metrics, tierKey, clearanceScore, siteWeather]
    );

    if (!ai || !metrics) {
      return (
        <VStack py={10} spacing={3}>
          <Icon as={MdAutoAwesome} boxSize={9} color="purple.300" />
          <Text fontSize="sm" fontWeight="700">No AI prediction for this well yet</Text>
          <Text fontSize="xs" color={subtextColor} textAlign="center" maxW="300px">
            The ML service evaluates wells with linked telemetry on its next cycle. Link a level sensor to enable
            flood and health forecasting.
          </Text>
        </VStack>
      );
    }

    const isStale = metrics.updatedAt !== null && Date.now() - metrics.updatedAt > staleAfterMs;
    const confidencePct = metrics.confidence !== null ? Math.round(metrics.confidence * 100) : null;
    const overtopClock =
      metrics.estMinutes !== null && metrics.estMinutes > 0
        ? new Date(Date.now() + metrics.estMinutes * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;
    const rainRisk = getRainfallRisk(siteWeather?.next24hRainMm);
    const rainAmplified = siteWeather && siteWeather.next24hRainMm >= 10 && metrics.probabilityPct >= 40;

    return (
      <VStack spacing={4} align="stretch">
        {/* Header: gauge + model meta */}
        <Flex
          p={4}
          borderRadius="xl"
          borderWidth="1.5px"
          borderColor={tier.color}
          bgGradient="linear(135deg, rgba(159,122,234,0.10), rgba(49,130,206,0.06))"
          align="center"
          gap={4}
        >
          <CircularProgress value={metrics.probabilityPct} size="88px" thickness="9px" color={tier.color} trackColor="blackAlpha.100">
            <CircularProgressLabel>
              <Text fontSize="lg" fontWeight="900" lineHeight="1">
                {metrics.probabilityPct}%
              </Text>
              <Text fontSize="8px" fontWeight="700" color={subtextColor} mt={0.5}>
                OVERFLOW
              </Text>
            </CircularProgressLabel>
          </CircularProgress>
          <Box flex="1">
            <HStack spacing={2} mb={1} flexWrap="wrap">
              <Badge colorScheme={tier.scheme} variant="solid" borderRadius="full" px={2} fontSize="2xs">
                {metrics.severity} · {tier.label.toUpperCase()}
              </Badge>
              {trendMeta && (
                <HStack spacing={0.5} color={trendMeta.color}>
                  <Icon as={trendMeta.icon} boxSize={4} />
                  <Text fontSize="2xs" fontWeight="700">
                    {trendMeta.label}
                    {metrics.probabilityDelta !== null
                      ? ` (${metrics.probabilityDelta > 0 ? '+' : ''}${Math.round(metrics.probabilityDelta * 100)} pts)`
                      : ''}
                  </Text>
                </HStack>
              )}
              {isStale && (
                <Badge colorScheme="orange" variant="subtle" fontSize="9px">
                  STALE
                </Badge>
              )}
            </HStack>
            <Text fontSize="xs" fontWeight="800">
              FlowSense AI Inundation & Asset Health Model
            </Text>
            <HStack spacing={3} mt={1} fontSize="2xs" color={subtextColor} flexWrap="wrap">
              {confidencePct !== null && (
                <HStack spacing={1}>
                  <CircularProgress value={confidencePct} size="14px" thickness="14px" color="purple.400" />
                  <Text fontWeight="700">{confidencePct}% confidence</Text>
                </HStack>
              )}
              {metrics.modelVersion && <Text>Model {metrics.modelVersion}</Text>}
              {metrics.updatedAt && (
                <HStack spacing={0.5}>
                  <Icon as={MdUpdate} boxSize={3} />
                  <Text>{formatRelativeTime(metrics.updatedAt)}</Text>
                </HStack>
              )}
            </HStack>
          </Box>
        </Flex>

        {/* KPI tiles */}
        <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={2}>
          {[
            {
              label: 'Time to Overtop',
              value: metrics.estMinutes === null ? 'N/A' : metrics.estMinutes <= 0 ? 'IMMINENT' : formatMinutesShort(metrics.estMinutes),
              hint: overtopClock ? `≈ ${overtopClock}` : null,
              icon: MdTimer,
              color: metrics.estMinutes !== null && metrics.estMinutes <= 60 ? 'red.500' : undefined,
            },
            {
              label: 'Rise Velocity',
              value: metrics.rateOfRise ? `${metrics.rateOfRise.toFixed(3)}` : '0.000',
              hint: '%/min',
              icon: MdSpeed,
              color: metrics.rateOfRise > 0 ? 'orange.400' : undefined,
            },
            {
              label: 'Health Score',
              value: `${metrics.healthScore}%`,
              hint: metrics.healthScore < 50 ? 'Below threshold' : 'Nominal',
              icon: MdFavorite,
              color: metrics.healthScore < 50 ? 'red.400' : 'teal.400',
            },
            {
              label: 'Remaining Life',
              value: `${metrics.rulDays}d`,
              hint: 'Predicted RUL',
              icon: MdShield,
              color: metrics.rulDays < 90 ? 'orange.400' : undefined,
            },
          ].map((k) => (
            <Box key={k.label} p={2.5} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor} bg={glassCardBg}>
              <HStack spacing={1} color={subtextColor}>
                <Icon as={k.icon} boxSize={3} />
                <Text fontSize="9px" fontWeight="700" textTransform="uppercase">
                  {k.label}
                </Text>
              </HStack>
              <Text fontSize="md" fontWeight="900" color={k.color} mt={0.5}>
                {k.value}
              </Text>
              {k.hint && (
                <Text fontSize="9px" color={subtextColor}>
                  {k.hint}
                </Text>
              )}
            </Box>
          ))}
        </SimpleGrid>

        {/* Forecast trajectory */}
        {metrics.forecast.length > 1 && (
          <Box p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
            <Flex justify="space-between" mb={1}>
              <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={subtextColor}>
                Overflow probability forecast
              </Text>
              <Text fontSize="9px" color={subtextColor}>
                dashed line = 85% critical threshold
              </Text>
            </Flex>
            <Box color={tier.color}>
              <AiSparkline values={metrics.forecast} width={380} height={56} threshold={0.85} strokeWidth={2} />
            </Box>
          </Box>
        )}

        {/* Model drivers */}
        {metrics.factors.length > 0 && (
          <Box p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
            <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={subtextColor} mb={2}>
              Top model drivers (explainability)
            </Text>
            <VStack spacing={1.5} align="stretch">
              {metrics.factors.map((f) => (
                <Box key={f.label}>
                  <Flex justify="space-between">
                    <Text fontSize="xs" fontWeight="600">
                      {f.label}
                    </Text>
                    {f.weight !== null && (
                      <Text fontSize="2xs" color={subtextColor}>
                        {f.weight.toFixed(2)}
                      </Text>
                    )}
                  </Flex>
                  {f.share !== null && (
                    <Progress value={f.share * 100} size="xs" colorScheme="purple" borderRadius="full" mt={0.5} />
                  )}
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* Site weather */}
        <Box p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
          <Flex justify="space-between" align="center" mb={2}>
            <HStack spacing={1.5}>
              <Icon as={MdGrain} color="blue.400" boxSize={4} />
              <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={subtextColor}>
                Site weather & rainfall coupling
              </Text>
            </HStack>
            <HStack spacing={1}>
              {siteWeather && (
                <Badge colorScheme={rainRisk.scheme} fontSize="9px" borderRadius="full">
                  {rainRisk.level}
                </Badge>
              )}
              <IconButton
                size="xs"
                variant="ghost"
                icon={<MdRefresh />}
                isLoading={weatherLoading}
                onClick={onReloadWeather}
                aria-label="Refresh site weather"
              />
            </HStack>
          </Flex>
          {!siteWeather ? (
            weatherLoading ? (
              <SkeletonText noOfLines={3} spacing={2} skeletonHeight="2" />
            ) : (
              <Text fontSize="xs" color={subtextColor}>
                {weatherError ? `Weather unavailable: ${weatherError.message}` : 'No weather data for this location.'}
              </Text>
            )
          ) : (
            <>
              <HStack spacing={4} mb={3} flexWrap="wrap">
                <WeatherStat
                  label="Now"
                  value={siteWeather.current.temperatureC !== null ? Math.round(siteWeather.current.temperatureC) : null}
                  unit="°C"
                  subtextColor={subtextColor}
                />
                <WeatherStat label="Next 6h" value={siteWeather.next6hRainMm} unit="mm" subtextColor={subtextColor} color="blue.400" />
                <WeatherStat label="Next 24h" value={siteWeather.next24hRainMm} unit="mm" subtextColor={subtextColor} color="blue.500" />
                <WeatherStat label="Past 24h" value={siteWeather.past24hRainMm} unit="mm" subtextColor={subtextColor} />
              </HStack>
              <HourlyRainBars hourly={siteWeather.hourly} subtextColor={subtextColor} emptyBarColor={emptyBarColor} />
              {siteWeather.daily.length > 0 && (
                <Wrap spacing={2} mt={3}>
                  {siteWeather.daily.map((d) => (
                    <WrapItem key={d.date}>
                      <Tag size="sm" borderRadius="full" variant="subtle" colorScheme={getRainfallRisk(d.rainMm).scheme}>
                        <TagLeftIcon as={MdOpacity} boxSize={3} />
                        <TagLabel fontSize="9px">
                          {new Date(`${d.date}T12:00:00`).toLocaleDateString([], { weekday: 'short' })} · {d.rainMm} mm
                          {d.prob !== null ? ` · ${d.prob}%` : ''}
                        </TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
              {rainAmplified && (
                <HStack mt={3} spacing={1.5} p={2} borderRadius="md" bg="rgba(66,153,225,0.10)">
                  <Icon as={MdAutoAwesome} color="purple.400" boxSize={3.5} />
                  <Text fontSize="2xs" fontWeight="600">
                    Rain-amplified risk: {siteWeather.next24hRainMm} mm forecast on top of a {metrics.probabilityPct}% model
                    probability. Expect faster rise than the current velocity suggests.
                  </Text>
                </HStack>
              )}
            </>
          )}
        </Box>

        {/* Recommended actions */}
        <Box p={3} borderRadius="lg" borderWidth="1px" borderColor="purple.300" bg="rgba(159,122,234,0.06)">
          <HStack spacing={1.5} mb={2}>
            <Icon as={MdLightbulbOutline} color="purple.400" boxSize={4} />
            <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={subtextColor}>
              {metrics.recommendations.length ? 'AI recommended actions' : 'Suggested actions (rule-based)'}
            </Text>
          </HStack>
          <VStack align="stretch" spacing={1.5}>
            {actions.map((a, i) => (
              <HStack key={a} align="start" spacing={2}>
                <Badge colorScheme="purple" borderRadius="full" fontSize="9px" minW="18px" textAlign="center">
                  {i + 1}
                </Badge>
                <Text fontSize="xs">{a}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        <Flex justify="space-between" align="center" gap={2}>
          <Text fontSize="9px" color={subtextColor} flex="1">
            Predictions are model estimates. Verify on site before operational decisions.
          </Text>
          <Button
            size="xs"
            variant="outline"
            colorScheme="purple"
            leftIcon={<MdContentCopy />}
            onClick={() => onCopySummary(well, metrics, siteWeather, actions)}
          >
            Copy AI summary
          </Button>
        </Flex>
      </VStack>
    );
  }
);
WellAiInsightsPanel.displayName = 'WellAiInsightsPanel';
// --- END UPDATE

const WellManagementPanel = ({
  wells = [],
  sensors = [],
  readings = {},
  selectedWellId = null,
  aiPredictions = {},
  onSelectWell,
  isLoading = false,
  onRefresh,
  onUpdateWellMetadata,
  // --- UPDATE: optional enterprise props (all backwards compatible)
  weather = null,
  enableLiveWeather = true,
  weatherLocation = null,
  weatherRefreshMs = WEATHER_REFRESH_MS_DEFAULT,
  pageSize = DEFAULT_PAGE_SIZE,
  staleAfterMs = STALE_PREDICTION_MS,
  // --- END UPDATE
  // --- UPDATE (wells v1.3): Well Intelligence feed (Map id → well with drainage / inspection) + actions
  intelById = null,
  onOpenExplainer,
  onCreateWorkOrder,
  // --- END UPDATE
}) => {
  const toast = useToast();
  // --- UPDATE
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const prefersReducedMotion = usePrefersReducedMotion();
  // --- END UPDATE

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [casingFilter, setCasingFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all'); // --- UPDATE (wells v1.3): all | drainage | water
  // --- UPDATE: AI filter / sort / incremental rendering / drawer tab
  const [aiFilter, setAiFilter] = useState('all');
  const [sortMode, setSortMode] = useState('default');
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [drawerTabIndex, setDrawerTabIndex] = useState(0);
  // Keeps typing smooth on large registers (React 18); falls back to the raw value on older React
  const deferredSearchQuery =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    typeof React.useDeferredValue === 'function' ? React.useDeferredValue(searchQuery) : searchQuery;
  // --- END UPDATE

  const [selectedWell, setSelectedWell] = useState(null);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);
  const [isPhotoPreviewOpen, setIsPhotoPreviewOpen] = useState(false);
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState('');
  const [previewWellTitle, setPreviewWellTitle] = useState('');

  const [inspectionForm, setInspectionForm] = useState({
    inspectorName: '',
    inspectorRole: 'technician',
    inspectionDate: new Date().toISOString().split('T')[0],
    lastCleanDate: new Date().toISOString().split('T')[0],
    baseDepthM: '',
    wellDepthM: '',
    casingCondition: 'good',
    notes: '',
    photoUrl: '',
    operationalStatus: 'operational'
  });
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (selectedWell) {
      const fresh = wells.find((w) => String(w.id) === String(selectedWell.id));
      if (fresh) setSelectedWell(fresh);
    }
  }, [wells]);

  const [isCalibrateModalOpen, setIsCalibrateModalOpen] = useState(false);
  const [calibrationBaseDepth, setCalibrationBaseDepth] = useState('');
  const [isCalibrating, setIsCalibrating] = useState(false);

  const handleOpenCalibrateModal = (well) => {
    setSelectedWell(well);
    const meta = well.metadata || {};
    setCalibrationBaseDepth(meta.base_depth_m || well.elevation || '');
    setIsCalibrateModalOpen(true);
  };

  const handleSaveCalibration = async () => {
    if (!selectedWell) return;
    const depthNum = parseFloat(calibrationBaseDepth);
    if (isNaN(depthNum) || depthNum <= 0) {
      toast({
        title: 'Invalid Depth',
        description: 'Please enter a valid positive base depth in meters.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCalibrating(true);
    try {
      let freshMeta = null;
      try {
        const res = await api.patch(`/assets/${selectedWell.id}/calibrate-depth`, {
          base_depth_m: depthNum
        });
        freshMeta = res.data?.metadata || null;
      } catch (err) {
        if (onUpdateWellMetadata) {
          const currentMeta = typeof selectedWell.metadata === 'string'
            ? JSON.parse(selectedWell.metadata || '{}')
            : (selectedWell.metadata || {});
          
          const measured = currentMeta.well_depth_m !== undefined && currentMeta.well_depth_m !== null
            ? parseFloat(currentMeta.well_depth_m)
            : null;

          let variance = 0;
          let score = 100;
          if (depthNum > 0 && measured !== null) {
            variance = parseFloat((measured - depthNum).toFixed(2));
            score = Math.min(100, Math.max(0, Math.round((measured / depthNum) * 100)));
          }

          freshMeta = {
            ...currentMeta,
            base_depth_m: depthNum,
            latest_variance_m: variance,
            latest_clearance_score: score
          };

          await onUpdateWellMetadata(selectedWell.id, {
            metadata: freshMeta,
            elevation: depthNum
          });
        } else {
          throw err;
        }
      }

      const updatedMetadata = freshMeta || {
        ...(typeof selectedWell.metadata === 'string'
          ? JSON.parse(selectedWell.metadata || '{}')
          : (selectedWell.metadata || {})),
        base_depth_m: depthNum
      };

      setSelectedWell((prev) => ({
        ...prev,
        elevation: depthNum,
        metadata: updatedMetadata
      }));

      setInspectionForm((prev) => ({
        ...prev,
        baseDepthM: depthNum
      }));

      toast({
        title: 'Well Benchmark Calibrated',
        description: `Base design depth set to ${depthNum}m for ${selectedWell.name || selectedWell.id}`,
        status: 'success',
        duration: 3500,
        isClosable: true,
      });

      if (onRefresh) onRefresh();
      setIsCalibrateModalOpen(false);
    } catch (err) {
      toast({
        title: 'Calibration Failed',
        description: err.response?.data?.message || err.message || 'Could not update base depth.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsCalibrating(false);
    }
  };

  const cardBorderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const theadBg = useColorModeValue('gray.50', 'gray.900');
  const modalBg = useColorModeValue('white', 'gray.800');
  const drawerBg = useColorModeValue('white', 'gray.850');
  const telemetryCardBg = useColorModeValue('blue.50', 'blue.950');
  const selectedRowBg = useColorModeValue('teal.50', 'rgba(49, 151, 149, 0.18)');
  const statBoxBg = useColorModeValue('white', 'gray.800');
  const glassCardBg = useColorModeValue('whiteAlpha.900', 'gray.800');
  // --- UPDATE: hoisted (was called inline inside JSX) + new tokens
  const aiCardBorderColor = useColorModeValue('purple.200', 'purple.800');
  const emptyBarColor = useColorModeValue('gray.300', 'gray.600');
  const aiHeaderBg = useColorModeValue('purple.50', 'rgba(159, 122, 234, 0.10)');

  // O(S) sensor index built once per sensors change, instead of filtering every sensor for every well.
  // Buckets keep the original sensor order, so the "first sensor with a reading" rule is unchanged.
  const sensorIndex = useMemo(() => {
    const index = new Map();
    (Array.isArray(sensors) ? sensors : []).forEach((s) => {
      const keys = new Set([String(s.asset_id), String(s.linked_entity_id)]);
      keys.forEach((k) => {
        const bucket = index.get(k);
        if (bucket) bucket.push(s);
        else index.set(k, [s]);
      });
    });
    return index;
  }, [sensors]);
  // --- END UPDATE

  // --- UPDATE (wells v1.3)
  const kindOf = useCallback((w) => {
    const iw = intelById?.get?.(String(w.id));
    if (iw?.well_kind || w.well_kind) return iw?.well_kind || w.well_kind;
    const t = String(w.asset_type || w.type || '').toLowerCase();
    return isWellType(t) && /drain|storm|soak|disposal|recharge/.test(t) ? 'drainage' : 'water';
  }, [intelById]);
  // --- END UPDATE

  const wellsWithSensors = useMemo(() => {
    return wells.map((well) => {
      // --- UPDATE: indexed lookup — same result as the previous sensors.filter(...)
      const linkedSensors = sensorIndex.get(String(well.id)) || [];
      // --- END UPDATE

      let liveLevel = null;
      let liveTimestamp = null;
      let matchedSensorId = null;

      for (const s of linkedSensors) {
        const dId = s.device_id || s.id;
        const reading = readings[dId];
        if (reading && reading.level) {
          liveLevel = reading.level;
          liveTimestamp = reading.timestamp;
          matchedSensorId = dId;
          break;
        }
      }

      return {
        ...well,
        linkedSensors,
        liveLevel,
        liveTimestamp,
        matchedSensorId
      };
    });
    // --- UPDATE
  }, [wells, sensorIndex, readings]);
  // --- END UPDATE

  const filteredWells = useMemo(() => {
    return wellsWithSensors.filter((well) => {
      // --- UPDATE: deferred query
      const q = deferredSearchQuery.toLowerCase().trim();
      // --- END UPDATE
      const meta = well.metadata || {};

      const nameMatch =
        (well.name || '').toLowerCase().includes(q) ||
        String(well.id || '').toLowerCase().includes(q) ||
        (well.location_description || '').toLowerCase().includes(q) ||
        well.linkedSensors.some((s) => (s.device_id || s.id || '').toLowerCase().includes(q));

      const statusMatch =
        statusFilter === 'all' ||
        (well.status || '').toLowerCase() === statusFilter.toLowerCase();

      const casingMatch =
        casingFilter === 'all' ||
        (meta.casing_condition || 'good').toLowerCase() === casingFilter.toLowerCase();

      // --- UPDATE (wells v1.3): drainage wells vs other wells (register type or intel kind)
      const isDrainage = kindOf(well) === 'drainage';
      const kindMatch = kindFilter === 'all' || (kindFilter === 'drainage') === isDrainage;
      return nameMatch && statusMatch && casingMatch && kindMatch;
      // --- END UPDATE
    });
    // --- UPDATE
  }, [wellsWithSensors, deferredSearchQuery, statusFilter, casingFilter, kindFilter, kindOf]);
  // --- END UPDATE

  const stats = useMemo(() => {
    const total = wells.length;
    let operational = 0;
    let critical = 0;
    let withPhotos = 0;

    wells.forEach((w) => {
      if ((w.status || '').toLowerCase() === 'operational') operational++;
      const casing = (w.metadata?.casing_condition || '').toLowerCase();
      if (casing === 'critical') critical++;
      if (w.metadata?.work_photo_url) withPhotos++;
    });

    // --- UPDATE (wells v1.3)
    const drainage = wells.filter((w) => kindOf(w) === 'drainage').length;
    return { total, operational, critical, withPhotos, drainage };
  }, [wells, kindOf]);

  // --- UPDATE: AI metrics computed once per prediction update, shared by filter / sort / rows
  const aiMetricsById = useMemo(() => {
    const map = new Map();
    wells.forEach((w) => {
      const ai = getAiForWell(w, aiPredictions);
      if (!ai) return;
      const metrics = extractAiMetrics(ai);
      map.set(String(w.id), { ai, metrics, tier: getAiRiskTier(metrics) });
    });
    return map;
  }, [wells, aiPredictions]);

  const aiTierCounts = useMemo(() => {
    const counts = { critical: 0, high: 0, watch: 0, stable: 0, unevaluated: 0 };
    wells.forEach((w) => {
      const entry = aiMetricsById.get(String(w.id));
      if (!entry) counts.unevaluated += 1;
      else counts[entry.tier] += 1;
    });
    return counts;
  }, [wells, aiMetricsById]);

  const displayWells = useMemo(() => {
    let list = filteredWells;
    if (aiFilter !== 'all') {
      list = list.filter((w) => {
        const entry = aiMetricsById.get(String(w.id));
        if (aiFilter === 'unevaluated') return !entry;
        return entry && entry.tier === aiFilter;
      });
    }
    if (sortMode !== 'default') {
      const metricOf = (w) => aiMetricsById.get(String(w.id))?.metrics || null;
      list = [...list].sort((a, b) => {
        const ma = metricOf(a);
        const mb = metricOf(b);
        switch (sortMode) {
          case 'risk':
            return (mb ? mb.probability : -1) - (ma ? ma.probability : -1);
          case 'overtop': {
            const ea = ma && ma.estMinutes !== null ? ma.estMinutes : Infinity;
            const eb = mb && mb.estMinutes !== null ? mb.estMinutes : Infinity;
            return ea - eb;
          }
          case 'health':
            return (ma ? ma.healthScore : Infinity) - (mb ? mb.healthScore : Infinity);
          case 'name':
            return String(a.name || a.id).localeCompare(String(b.name || b.id), undefined, { numeric: true });
          default:
            return 0;
        }
      });
    }
    return list;
  }, [filteredWells, aiFilter, sortMode, aiMetricsById]);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [deferredSearchQuery, statusFilter, casingFilter, kindFilter, aiFilter, sortMode, pageSize]);

  const pagedWells = useMemo(() => displayWells.slice(0, visibleCount), [displayWells, visibleCount]);
  const remainingCount = displayWells.length - pagedWells.length;
  const animateRows = !prefersReducedMotion && pagedWells.length <= ROW_ANIMATION_LIMIT;

  const handleLoadMore = useCallback(() => setVisibleCount((c) => c + pageSize), [pageSize]);

  const handleResetAiView = useCallback(() => {
    setAiFilter('all');
    setSortMode('default');
  }, []);

  // ── Live weather: fleet (well-field centroid) + selected well site ──
  const fleetWeatherLocation = useMemo(() => {
    const custom = weatherLocation ? parseCoordPair(weatherLocation.lat, weatherLocation.lng) : null;
    if (custom) return { ...custom, label: weatherLocation.label || 'Custom location' };
    let sumLat = 0;
    let sumLng = 0;
    let n = 0;
    wells.forEach((w) => {
      const c = parseCoordPair(w.latitude, w.longitude);
      if (c) {
        sumLat += c.lat;
        sumLng += c.lng;
        n += 1;
      }
    });
    if (!n) return DEFAULT_WEATHER_LOCATION;
    return {
      lat: Math.round((sumLat / n) * 1000) / 1000,
      lng: Math.round((sumLng / n) * 1000) / 1000,
      label: `Well field (${n} sites)`,
    };
  }, [wells, weatherLocation]);

  const fleetWeatherState = useLiveWeather(fleetWeatherLocation.lat, fleetWeatherLocation.lng, {
    enabled: enableLiveWeather && !weather,
    refreshMs: weatherRefreshMs,
  });
  const fleetWeather = weather || fleetWeatherState.data;

  const selectedWellCoords = useMemo(
    () => (selectedWell ? parseCoordPair(selectedWell.latitude, selectedWell.longitude) : null),
    [selectedWell?.latitude, selectedWell?.longitude] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const siteWeatherState = useLiveWeather(selectedWellCoords?.lat, selectedWellCoords?.lng, {
    enabled: enableLiveWeather && !weather && isDetailsDrawerOpen && !!selectedWellCoords,
    refreshMs: weatherRefreshMs,
  });
  const siteWeather = weather || siteWeatherState.data || fleetWeather;

  const fleetWeatherInsight = useMemo(() => {
    if (!fleetWeather) return null;
    const rain24 = fleetWeather.next24hRainMm;
    let exposed = 0;
    aiMetricsById.forEach((entry) => {
      if (entry.tier !== 'stable') exposed += 1;
    });
    if (exposed > 0 && rain24 >= 10) {
      return `${exposed} AI-flagged well(s) exposed to ${rain24} mm forecast rainfall — prioritise clearance checks.`;
    }
    if (rain24 >= 25) return 'Heavy rain forecast — expect rising levels; monitor low-clearance wells.';
    if (exposed > 0) return `${exposed} AI-flagged well(s); rainfall outlook is not expected to amplify risk.`;
    return 'Rainfall outlook is benign for the well field.';
  }, [fleetWeather, aiMetricsById]);

  const handleOpenAiInsights = useCallback(
    (well) => {
      setSelectedWell(well);
      if (onSelectWell) onSelectWell(well);
      setDrawerTabIndex(2);
      setIsDetailsDrawerOpen(true);
    },
    [onSelectWell]
  );

  useEffect(() => {
    if (!isDetailsDrawerOpen) setDrawerTabIndex(0);
  }, [isDetailsDrawerOpen]);

  const handleCopyAiSummary = useCallback(
    async (well, metrics, site, actions) => {
      if (!well || !metrics) return;
      const lines = [
        `FlowSense AI — ${well.name || `Well ${well.id}`} (Asset ${well.id})`,
        `Overflow probability: ${metrics.probabilityPct}% (${metrics.severity})`,
        `Time to overtop: ${metrics.estMinutes === null ? 'N/A' : formatMinutesShort(metrics.estMinutes)}`,
        `Rise velocity: ${(metrics.rateOfRise || 0).toFixed(3)}%/min`,
        `Health: ${metrics.healthScore}% | RUL: ${metrics.rulDays} days`,
        metrics.confidence !== null ? `Model confidence: ${Math.round(metrics.confidence * 100)}%` : null,
        site ? `Rainfall: ${site.next6hRainMm} mm (6h) / ${site.next24hRainMm} mm (24h) forecast` : null,
        actions?.length ? `Actions:\n${actions.map((a, i) => `  ${i + 1}. ${a}`).join('\n')}` : null,
        `Generated ${new Date().toLocaleString()}`,
      ].filter(Boolean);
      try {
        await navigator.clipboard.writeText(lines.join('\n'));
        if (isMounted.current) {
          toast({ title: 'AI summary copied', status: 'success', duration: 2000, position: 'bottom-right' });
        }
      } catch {
        if (isMounted.current) {
          toast({
            title: 'Copy failed',
            description: 'Clipboard access is blocked in this browser.',
            status: 'warning',
            duration: 3000,
            position: 'bottom-right',
          });
        }
      }
    },
    [toast]
  );
  // --- END UPDATE

  const handleOpenDetails = useCallback(
    (well) => {
      setSelectedWell(well);
      if (onSelectWell) onSelectWell(well);
      setIsDetailsDrawerOpen(true);
    },
    [onSelectWell]
  );

  const handleOpenInspectionModal = useCallback(
    (well) => {
      setSelectedWell(well);
      if (onSelectWell) onSelectWell(well);
      const meta = well.metadata || {};

      setInspectionForm({
        inspectorName: meta.last_inspected_by || '',
        inspectorRole: meta.inspector_role || 'technician',
        inspectionDate: meta.last_inspected_date || new Date().toISOString().split('T')[0],
        lastCleanDate: meta.last_cleaned_date || '',
        baseDepthM: meta.base_depth_m || well.elevation || '',
        wellDepthM: meta.well_depth_m !== undefined && meta.well_depth_m !== null ? meta.well_depth_m : '',
        casingCondition: meta.casing_condition || 'good',
        notes: meta.inspection_notes || '',
        photoUrl: meta.work_photo_url || '',
        operationalStatus: well.status || 'operational'
      });
      setIsInspectionModalOpen(true);
    },
    [onSelectWell]
  );

  const handleSaveInspection = async () => {
    if (!selectedWell) return;
    setIsSaving(true);

    try {
      const currentMeta = typeof selectedWell.metadata === 'string'
        ? JSON.parse(selectedWell.metadata || '{}')
        : (selectedWell.metadata || {});

      const resolvedBaseDepth = inspectionForm.baseDepthM !== '' && !isNaN(parseFloat(inspectionForm.baseDepthM))
        ? parseFloat(inspectionForm.baseDepthM)
        : (currentMeta.base_depth_m ? parseFloat(currentMeta.base_depth_m) : parseFloat(selectedWell.elevation) || 0);

      const resolvedMeasuredDepth = inspectionForm.wellDepthM !== '' && !isNaN(parseFloat(inspectionForm.wellDepthM))
        ? parseFloat(inspectionForm.wellDepthM)
        : null;

      const payload = {
        base_depth_m: resolvedBaseDepth,
        well_depth_m: resolvedMeasuredDepth,
        last_inspected_by: inspectionForm.inspectorName.trim(),
        inspector_role: inspectionForm.inspectorRole,
        last_inspected_date: inspectionForm.inspectionDate,
        last_cleaned_date: inspectionForm.lastCleanDate || null,
        casing_condition: inspectionForm.casingCondition,
        inspection_notes: inspectionForm.notes.trim(),
        work_photo_url: inspectionForm.photoUrl.trim(),
        status: inspectionForm.operationalStatus
      };

      const res = await api.patch(`/assets/${selectedWell.id}/well-inspection`, payload);

      const updatedMetadata = res.data?.metadata || {
        ...currentMeta,
        ...payload
      };

      setSelectedWell((prev) => ({
        ...prev,
        elevation: resolvedBaseDepth,
        status: inspectionForm.operationalStatus,
        metadata: updatedMetadata
      }));

      toast({
        title: 'Inspection Saved Successfully',
        description: `Updated well register specifications for ${selectedWell.name || selectedWell.id}`,
        status: 'success',
        duration: 3500,
        isClosable: true,
        position: 'bottom-right'
      });

      if (onRefresh) onRefresh();
      setIsInspectionModalOpen(false);
    } catch (err) {
      toast({
        title: 'Inspection Save Failed',
        description: err.response?.data?.message || err.message || 'An unexpected error occurred while saving well record.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTriggerPhotoZoom = (url, title) => {
    const resolved = resolvePhotoUrl(url);
    if (!resolved) return;
    setPreviewPhotoUrl(resolved);
    setPreviewWellTitle(title || 'Well Work Verification');
    setIsPhotoPreviewOpen(true);
  };

  return (
    <VStack spacing={5} align="stretch" w="100%">
      {/* ═══ HIGH-LEVEL OPERATIONAL METRICS (NOW INCLUDES 5TH AI STAT CARD) ═══ */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
        <GlassPanel p={4}>
          <Stat>
            <StatLabel fontSize="xs" fontWeight="700" color={subtextColor}>
              Total Wells In Register
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="800" color="teal.500">
              {stats.total}
            </StatNumber>
            <StatHelpText fontSize="xs" mb={0}>
              {/* --- UPDATE (wells v1.3) */}
              {stats.drainage ? `${stats.drainage} drainage · ${stats.total - stats.drainage} other wells` : 'Municipal extraction points'}
            </StatHelpText>
          </Stat>
        </GlassPanel>

        <GlassPanel p={4}>
          <Stat>
            <StatLabel fontSize="xs" fontWeight="700" color={subtextColor}>
              Operational Status
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="800" color="green.500">
              {stats.operational}
            </StatNumber>
            <StatHelpText fontSize="xs" mb={0}>
              Online & functioning
            </StatHelpText>
          </Stat>
        </GlassPanel>

        <GlassPanel p={4}>
          <Stat>
            <StatLabel fontSize="xs" fontWeight="700" color={subtextColor}>
              Casing Structural Alerts
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="800" color="red.500">
              {stats.critical}
            </StatNumber>
            <StatHelpText fontSize="xs" mb={0}>
              Critical attention needed
            </StatHelpText>
          </Stat>
        </GlassPanel>

        <GlassPanel p={4}>
          <Stat>
            <StatLabel fontSize="xs" fontWeight="700" color={subtextColor}>
              Verified Site Photos
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="800" color="blue.500">
              {stats.withPhotos}
            </StatNumber>
            <StatHelpText fontSize="xs" mb={0}>
              Inspections logged with media
            </StatHelpText>
          </Stat>
        </GlassPanel>

        {/* 🧠 5TH COMPONENT: AI PREDICTION METRIC SUMMARY CARD */}
        {/* --- UPDATE: hoisted colour token + weather/selection/loading wiring */}
        <GlassPanel p={4} borderWidth="1.5px" borderColor={aiCardBorderColor}>
          <WellAiSummaryCard
            wells={wells}
            aiPredictions={aiPredictions}
            subtextColor={subtextColor}
            isLoading={isLoading}
            weather={fleetWeather}
            onSelectWell={handleOpenAiInsights}
            staleAfterMs={staleAfterMs}
          />
        </GlassPanel>
        {/* --- END UPDATE */}
      </SimpleGrid>

      {/* --- UPDATE: live weather × AI coupling strip */}
      {(enableLiveWeather || weather) && (
        <FleetWeatherStrip
          weather={fleetWeather}
          isLoading={fleetWeatherState.isLoading}
          error={weather ? null : fleetWeatherState.error}
          onReload={fleetWeatherState.reload}
          locationLabel={fleetWeatherLocation.label}
          insight={fleetWeatherInsight}
          subtextColor={subtextColor}
          cardBorderColor={cardBorderColor}
          emptyBarColor={emptyBarColor}
        />
      )}
      {/* --- END UPDATE */}

      {/* ═══ ACTIONS & SEARCH TOOLBAR ═══ */}
      <GlassPanel p={0} overflow="hidden">
        <Flex
          p={4}
          borderBottomWidth="1px"
          borderColor={cardBorderColor}
          justify="space-between"
          align="center"
          flexWrap="wrap"
          gap={3}
        >
          <HStack
            spacing={3}
            flex="1"
            minW={{ base: '100%', md: '480px' }}
            // --- UPDATE: allow the extra AI controls to wrap on narrow screens
            flexWrap="wrap"
            rowGap={2}
            // --- END UPDATE
          >
            <InputGroup size="sm" maxW={{ base: '100%', md: '300px' }}>
              <InputLeftElement pointerEvents="none">
                <MdSearch color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search well name, asset ID, or sensor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                borderRadius="xl"
              />
              {searchQuery && (
                <InputRightElement>
                  <IconButton
                    size="xs"
                    icon={<MdClose />}
                    variant="ghost"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear Search"
                  />
                </InputRightElement>
              )}
            </InputGroup>

            <Select
              size="sm"
              w="150px"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              borderRadius="xl"
            >
              <option value="all">All Statuses</option>
              <option value="operational">Operational</option>
              <option value="maintenance_required">Maintenance Req.</option>
              <option value="damaged">Damaged / Offline</option>
            </Select>

            <Select
              size="sm"
              w="150px"
              value={casingFilter}
              onChange={(e) => setCasingFilter(e.target.value)}
              borderRadius="xl"
            >
              <option value="all">All Casing Health</option>
              <option value="good">Sound / Good</option>
              <option value="degraded">Degraded</option>
              <option value="critical">Critical</option>
            </Select>

            {/* --- UPDATE (wells v1.3): well kind */}
            <Select size="sm" w="150px" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} borderRadius="xl" aria-label="Well kind">
              <option value="all">All Well Kinds</option>
              <option value="drainage">Drainage Wells ({stats.drainage})</option>
              <option value="water">Other Wells ({stats.total - stats.drainage})</option>
            </Select>
            {/* --- END UPDATE */}

            {/* --- UPDATE: AI risk filter + AI-aware sorting */}
            <Select
              size="sm"
              w="170px"
              value={aiFilter}
              onChange={(e) => setAiFilter(e.target.value)}
              borderRadius="xl"
              borderColor={aiFilter !== 'all' ? 'purple.400' : undefined}
              aria-label="Filter by AI risk tier"
            >
              <option value="all">All AI Risk Tiers</option>
              <option value="critical">AI Critical ({aiTierCounts.critical})</option>
              <option value="high">AI High ({aiTierCounts.high})</option>
              <option value="watch">AI Watch ({aiTierCounts.watch})</option>
              <option value="stable">AI Stable ({aiTierCounts.stable})</option>
              <option value="unevaluated">Not Evaluated ({aiTierCounts.unevaluated})</option>
            </Select>

            <Select
              size="sm"
              w="170px"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              borderRadius="xl"
              aria-label="Sort wells"
            >
              <option value="default">Sort: Register Order</option>
              <option value="risk">Sort: AI Risk (High → Low)</option>
              <option value="overtop">Sort: Time to Overtop</option>
              <option value="health">Sort: Health (Low → High)</option>
              <option value="name">Sort: Name (A → Z)</option>
            </Select>
            {/* --- END UPDATE */}
          </HStack>

          <HStack spacing={2}>
            {/* --- UPDATE: result count + reset */}
            <Text fontSize="xs" color={subtextColor} whiteSpace="nowrap">
              {pagedWells.length} of {displayWells.length} wells
            </Text>
            {(aiFilter !== 'all' || sortMode !== 'default') && (
              <Button size="xs" variant="ghost" colorScheme="purple" onClick={handleResetAiView}>
                Reset AI view
              </Button>
            )}
            {/* --- END UPDATE */}
            <Tooltip label="Refresh Well Register">
              <IconButton
                size="sm"
                icon={<MdRefresh />}
                onClick={onRefresh}
                isLoading={isLoading}
                aria-label="Refresh Wells"
                variant="ghost"
              />
            </Tooltip>
          </HStack>
        </Flex>

        {/* ═══ WELL ASSET DATA TABLE ═══ */}
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={theadBg}>
              <Tr>
                <Th>Well Identification</Th>
                <Th>Status</Th>
                <Th>Connected Sensor</Th>
                <Th>Live Water Level</Th>
                {/* 🧠 NEW AI ML PREDICTION COLUMN HEADER */}
                <Th
                  color="purple.500"
                  // --- UPDATE
                  bg={aiHeaderBg}
                  // --- END UPDATE
                >
                  <HStack spacing={1}>
                    <Icon as={MdAutoAwesome} boxSize={3.5} />
                    <Text>AI Prediction & Health</Text>
                  </HStack>
                </Th>
                <Th>Well Depth</Th>
                <Th>Last Inspected By</Th>
                <Th textAlign="center">Work Photo</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              <AnimatePresence>
                {/* --- UPDATE: skeleton rows on first load; AI-filtered + paged rows */}
                {isLoading && wells.length === 0 ? (
                  Array.from({ length: 5 }).map((_, sIdx) => (
                    <Tr key={`skeleton-${sIdx}`}>
                      {Array.from({ length: 9 }).map((__, cIdx) => (
                        <Td key={cIdx}>
                          <Skeleton h="14px" borderRadius="md" w={cIdx === 0 ? '120px' : '70px'} />
                        </Td>
                      ))}
                    </Tr>
                  ))
                ) : displayWells.length === 0 ? (
                /* --- END UPDATE */
                  <Tr>
                    <Td colSpan={9} py={8} textAlign="center">
                      <VStack spacing={2}>
                        <Icon as={MdSensors} boxSize={8} color="gray.400" />
                        <Text fontWeight="600" color={subtextColor}>
                          No well records match the current filter selection
                        </Text>
                        {/* --- UPDATE */}
                        {aiFilter !== 'all' && (
                          <Button size="xs" variant="outline" colorScheme="purple" onClick={handleResetAiView}>
                            Clear AI filter
                          </Button>
                        )}
                        {/* --- END UPDATE */}
                      </VStack>
                    </Td>
                  </Tr>
                ) : (
                  // --- UPDATE: render the paged, AI-filtered list
                  pagedWells.map((well, idx) => {
                  // --- END UPDATE
                    const meta = well.metadata || {};
                    const depth =
                      meta.well_depth_m !== undefined && meta.well_depth_m !== null
                        ? `${meta.well_depth_m} m`
                        : well.elevation
                        ? `${well.elevation} m`
                        : 'Unsurveyed';
                    const isSelected = selectedWellId && String(selectedWellId) === String(well.id);
                    const casingObj = getCasingBadgeDetails(meta.casing_condition);
                    const inspector = meta.last_inspected_by
                      ? `${meta.last_inspected_by} (${meta.inspector_role || 'Tech'})`
                      : 'Uninspected';

                    const photoSrc = resolvePhotoUrl(meta.work_photo_url);
                    // --- UPDATE
                    const aiTier = aiMetricsById.get(String(well.id))?.tier;
                    const aiCritical = aiTier === 'critical';
                    // --- END UPDATE

                    return (
                      <MotionTr
                        key={well.id || idx}
                        // --- UPDATE: skip per-row animation on large lists / reduced motion
                        initial={animateRows ? { opacity: 0, y: 6 } : false}
                        animate={{ opacity: 1, y: 0 }}
                        exit={animateRows ? { opacity: 0 } : undefined}
                        transition={{ delay: animateRows ? Math.min(idx * 0.02, 0.4) : 0 }}
                        // --- END UPDATE
                        bg={isSelected ? selectedRowBg : 'transparent'}
                        // --- UPDATE: red accent for AI-critical rows (selection styling unchanged)
                        borderLeftWidth={isSelected ? '4px' : aiCritical ? '3px' : '0px'}
                        borderLeftColor={isSelected ? 'teal.400' : 'red.400'}
                        // --- END UPDATE
                        cursor="pointer"
                        onClick={() => onSelectWell && onSelectWell(well)}
                        _hover={{ bg: isSelected ? selectedRowBg : hoverBg }}
                      >
                        {/* Well ID & Location */}
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="700" fontSize="sm">
                              {well.name || `Well ${well.id}`}
                            </Text>
                            {/* --- UPDATE (wells v1.3): kind + Well Intelligence + linked drainage at a glance */}
                            {(() => {
                              const iw = intelById?.get?.(String(well.id));
                              const tm = iw?.intel ? tierMeta(iw.intel.tier) : null;
                              const n = iw?.drainage?.nearby || [];
                              const lapsed = n.filter((d) => ['overdue', 'never_inspected'].includes(d.inspection?.status)).length;
                              return (
                                <HStack spacing={1} mt={0.5} flexWrap="wrap">
                                  {kindOf(well) === 'drainage' && <Badge fontSize="8px" colorScheme="cyan">{well.asset_type || 'Drainage well'}</Badge>}
                                  {tm && <Badge fontSize="8px" colorScheme={tm.scheme} variant="subtle">risk {iw.intel.score ?? '—'}</Badge>}
                                  {n.length > 0 && <Badge fontSize="8px" colorScheme={n.some((d) => d.impaired) ? 'red' : 'teal'} variant="outline">{n.length} drain{n.length > 1 ? 's' : ''}{lapsed ? ` · ${lapsed} uninspected` : ''}</Badge>}
                                </HStack>
                              );
                            })()}
                            {/* --- END UPDATE */}
                            <Text fontSize="xs" color={subtextColor}>
                              {well.location_description || `Asset ID: ${well.id}`}
                            </Text>
                          </VStack>
                        </Td>

                        {/* Operational Status */}
                        <Td>
                          <Badge
                            colorScheme={
                              well.status === 'operational'
                                ? 'green'
                                : well.status === 'maintenance_required'
                                ? 'orange'
                                : 'red'
                            }
                            borderRadius="full"
                            px={2}
                          >
                            {(well.status || 'Active').toUpperCase()}
                          </Badge>
                        </Td>

                        {/* Connected IoT Sensors */}
                        <Td>
                          {well.linkedSensors.length > 0 ? (
                            <VStack align="start" spacing={1}>
                              {well.linkedSensors.map((s) => (
                                <HStack key={s.device_id || s.id} spacing={1}>
                                  <Icon as={MdSensors} color="teal.400" boxSize={3.5} />
                                  <Text fontSize="xs" fontWeight="600">
                                    {s.device_id || s.id}
                                  </Text>
                                </HStack>
                              ))}
                            </VStack>
                          ) : (
                            <Text fontSize="xs" color={subtextColor}>
                              No Linked Sensor
                            </Text>
                          )}
                        </Td>

                        {/* Water Level Telemetry */}
                        <Td>
                          <VStack align="start" spacing={1}>
                            {well.liveLevel ? (
                              <Badge
                                colorScheme={getLevelBadgeColor(well.liveLevel)}
                                px={2}
                                py={0.5}
                                borderRadius="full"
                              >
                                LEVEL: {well.liveLevel}
                              </Badge>
                            ) : (
                              <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                                NO READING
                              </Badge>
                            )}

                            {well.liveTimestamp && (
                              <Text fontSize="10px" color={subtextColor}>
                                {new Date(well.liveTimestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Text>
                            )}
                          </VStack>
                        </Td>

                        {/* 🧠 NEW COMPONENT: AI PREDICTION STATUS CELL */}
                        <Td onClick={(e) => e.stopPropagation()}>
                          <WellAiStatusCell
                            well={well}
                            aiPredictions={aiPredictions}
                            subtextColor={subtextColor}
                            // --- UPDATE
                            weather={fleetWeather}
                            onOpenInsights={handleOpenAiInsights}
                            staleAfterMs={staleAfterMs}
                            // --- END UPDATE
                          />
                        </Td>

                        {/* Depth & Casing Structural Health */}
                        <Td>
                          <HStack spacing={1}>
                            <Icon as={MdWaterDrop} color="blue.400" boxSize={3.5} />
                            <Text fontSize="xs" fontWeight="700">
                              {depth}
                            </Text>
                          </HStack>
                          <Tag size="sm" colorScheme={casingObj.colorScheme} mt={1} borderRadius="full">
                            <TagLeftIcon as={casingObj.icon} boxSize={3} />
                            <TagLabel fontSize="9px">{casingObj.label}</TagLabel>
                          </Tag>
                        </Td>

                        {/* Inspector Details */}
                        <Td>
                          <HStack spacing={1}>
                            <Icon as={MdPerson} color="gray.400" boxSize={3.5} />
                            <Text fontSize="xs">{inspector}</Text>
                          </HStack>
                        </Td>

                        {/* Work Photo */}
                        <Td textAlign="center" onClick={(e) => e.stopPropagation()}>
                          {photoSrc ? (
                            <Tooltip label="Click to preview inspection photo" hasArrow>
                              <Box position="relative" display="inline-block">
                                <Image
                                  src={photoSrc}
                                  alt="Work Verification"
                                  boxSize="36px"
                                  objectFit="cover"
                                  borderRadius="md"
                                  borderWidth="1.5px"
                                  borderColor="teal.300"
                                  cursor="pointer"
                                  transition="all 0.2s"
                                  fallback={
                                    <IconButton
                                      size="xs"
                                      colorScheme="teal"
                                      variant="outline"
                                      icon={<MdBrokenImage />}
                                      aria-label="Image missing"
                                    />
                                  }
                                  _hover={{
                                    transform: 'scale(1.15)',
                                    borderColor: 'teal.500',
                                    boxShadow: 'md'
                                  }}
                                  onClick={() =>
                                    handleTriggerPhotoZoom(photoSrc, well.name || well.id)
                                  }
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tag size="sm" variant="subtle" colorScheme="gray" fontSize="10px">
                              No Photo
                            </Tag>
                          )}
                        </Td>

                        {/* Action Buttons */}
                        <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
                          <HStack spacing={1} justify="flex-end">
                            <Button
                              size="xs"
                              colorScheme="blue"
                              variant="ghost"
                              leftIcon={<MdInfo />}
                              onClick={() => handleOpenDetails(well)}
                            >
                              Details
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="purple"
                              variant="outline"
                              leftIcon={<MdBuild />}
                              onClick={() => handleOpenCalibrateModal(well)}
                            >
                              Calibrate
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="teal"
                              variant="outline"
                              leftIcon={<MdAssignmentTurnedIn />}
                              onClick={() => handleOpenInspectionModal(well)}
                            >
                              Inspect
                            </Button>
                          </HStack>
                        </Td>
                      </MotionTr>
                    );
                  })
                )}
              </AnimatePresence>
            </Tbody>
          </Table>
        </TableContainer>
        {/* --- UPDATE: incremental rendering */}
        {remainingCount > 0 && (
          <Flex justify="center" py={3} borderTopWidth="1px" borderColor={cardBorderColor}>
            <Button size="xs" variant="ghost" colorScheme="teal" leftIcon={<MdExpandMore />} onClick={handleLoadMore}>
              Load {Math.min(pageSize, remainingCount)} more ({remainingCount} remaining)
            </Button>
          </Flex>
        )}
        {/* --- END UPDATE */}
      </GlassPanel>

      {/* ═══ FULL WELL DATA INSPECTION DRAWER ═══ */}
      <Drawer
        isOpen={isDetailsDrawerOpen}
        placement="right"
        onClose={() => setIsDetailsDrawerOpen(false)}
        size="md"
      >
        <DrawerOverlay backdropFilter="blur(4px)" />
        <DrawerContent bg={drawerBg} borderLeftRadius="2xl">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor={cardBorderColor}>
            <HStack spacing={2}>
              <Icon as={MdWaterDrop} color="blue.400" />
              <Box>
                <Heading size="md">{selectedWell?.name || selectedWell?.id}</Heading>
                <Text fontSize="xs" color={subtextColor}>
                  System Asset ID: {selectedWell?.id}
                </Text>
              </Box>
            </HStack>
          </DrawerHeader>

          <DrawerBody py={5}>
            {selectedWell && (() => {
              const meta = typeof selectedWell.metadata === 'string'
                ? JSON.parse(selectedWell.metadata || '{}')
                : (selectedWell.metadata || {});

              const history = Array.isArray(meta.inspection_history) ? meta.inspection_history : [];

              const rawBase = meta.base_depth_m !== undefined && meta.base_depth_m !== null && meta.base_depth_m !== ''
                ? parseFloat(meta.base_depth_m)
                : (selectedWell.elevation !== null && selectedWell.elevation !== undefined && selectedWell.elevation !== ''
                    ? parseFloat(selectedWell.elevation)
                    : null);

              const baseDepth = rawBase !== null && !isNaN(rawBase) && rawBase > 0
                ? `${rawBase} m`
                : 'Unrecorded';

              const rawMeasured = meta.well_depth_m !== undefined && meta.well_depth_m !== null && meta.well_depth_m !== ''
                ? parseFloat(meta.well_depth_m)
                : null;

              const currentDepth = rawMeasured !== null && !isNaN(rawMeasured)
                ? `${rawMeasured} m`
                : 'Pending Inspection';

              let latestVariance = meta.latest_variance_m ?? 0;
              let latestScore = meta.latest_clearance_score ?? 100;

              if (rawBase !== null && rawBase > 0 && rawMeasured !== null) {
                latestVariance = parseFloat((rawMeasured - rawBase).toFixed(2));
                latestScore = Math.min(100, Math.max(0, Math.round((rawMeasured / rawBase) * 100)));
              }

              return (
                <Tabs
                  variant="enclosed"
                  colorScheme="teal"
                  // --- UPDATE: controlled so the AI tab can be opened directly; lazy panels
                  index={drawerTabIndex}
                  onChange={setDrawerTabIndex}
                  isLazy
                  // --- END UPDATE
                >
                  <TabList mb={4}>
                    <Tab fontSize="xs" fontWeight="700">Overview & Telemetry</Tab>
                    <Tab fontSize="xs" fontWeight="700">
                      Audit Ledger ({history.length})
                    </Tab>
                    {/* --- UPDATE */}
                    <Tab fontSize="xs" fontWeight="700" color="purple.500" _selected={{ color: 'purple.600', borderColor: 'inherit', borderBottomColor: 'transparent' }}>
                      <HStack spacing={1}>
                        <Icon as={MdAutoAwesome} boxSize={3} />
                        <Text>AI & Weather</Text>
                      </HStack>
                    </Tab>
                    {/* --- END UPDATE */}
                    {/* --- UPDATE (wells v1.3) */}
                    <Tab fontSize="xs" fontWeight="700">Drainage & Network</Tab>
                    {/* --- END UPDATE */}
                  </TabList>

                  <TabPanels>
                    <TabPanel p={0}>
                      <VStack spacing={5} align="stretch">
                        <Box
                          p={4}
                          borderRadius="xl"
                          bg={telemetryCardBg}
                          borderWidth="1px"
                          borderColor="blue.400"
                        >
                          <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color="blue.500">
                            Live Water Telemetry
                          </Text>
                          <Flex justify="space-between" align="center" mt={2}>
                            <Box>
                              <Text fontSize="xs" color={subtextColor}>Water Level State</Text>
                              <Badge
                                colorScheme={getLevelBadgeColor(selectedWell.liveLevel)}
                                fontSize="md"
                                px={3}
                                py={1}
                                borderRadius="lg"
                                mt={1}
                              >
                                {selectedWell.liveLevel || 'NO SIGNAL'}
                              </Badge>
                            </Box>
                            <Box textAlign="right">
                              <Text fontSize="xs" color={subtextColor}>Connected Sensor</Text>
                              <Text fontSize="sm" fontWeight="bold">
                                {selectedWell.matchedSensorId || 'None'}
                              </Text>
                            </Box>
                          </Flex>
                        </Box>

                        <Box p={4} borderRadius="xl" borderWidth="1px" borderColor={cardBorderColor} bg={glassCardBg}>
                          <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color={subtextColor} mb={2}>
                            Wellbore Clearance Score
                          </Text>
                          <Flex justify="space-between" align="center">
                            <Box>
                              <HStack spacing={2}>
                                <Text fontSize="2xl" fontWeight="900" color={latestScore < 80 ? 'red.500' : 'teal.500'}>
                                  {latestScore}%
                                </Text>
                                <Badge colorScheme={latestScore < 80 ? 'red' : 'green'}>
                                  {latestScore < 80 ? 'SILT BUILDUP ALERT' : 'OPTIMAL CLEARANCE'}
                                </Badge>
                              </HStack>
                              <Text fontSize="xs" color={subtextColor} mt={1}>
                                Base Design: <b>{baseDepth} m</b> | Current: <b>{currentDepth}</b>
                              </Text>
                            </Box>
                            <Box textAlign="right">
                              <Text fontSize="xs" color={subtextColor}>Variance</Text>
                              <Text fontSize="sm" fontWeight="800" color={latestVariance < 0 ? 'orange.500' : 'gray.600'}>
                                {latestVariance > 0 ? `+${latestVariance} m` : `${latestVariance} m`}
                              </Text>
                            </Box>
                          </Flex>
                        </Box>

                        {/* 🧠 DRAWER EMBEDDED AI PREDICTION CARD */}
                        {(() => {
                          const aiInfo = aiPredictions[String(selectedWell.id)] || selectedWell.ai_predictions;
                          if (!aiInfo) return null;

                          const prob = aiInfo.floodRiskProbability ?? aiInfo.flood_probability ?? 0;
                          const sev = (aiInfo.predictedSeverity || aiInfo.flood_risk || 'NONE').toUpperCase();
                          const isCritical = prob >= 0.85 || sev === 'CRITICAL';
                          const isHigh = prob >= 0.65 || sev === 'HIGH';
                          const estMins = aiInfo.estimatedMinutesToOvertopping ?? aiInfo.est_minutes_to_overtopping;

                          return (
                            <Box
                              p={4}
                              borderRadius="xl"
                              borderWidth="1.5px"
                              borderColor={isCritical ? 'red.500' : isHigh ? 'orange.400' : 'purple.400'}
                              bg={isCritical ? 'rgba(239, 68, 68, 0.08)' : 'rgba(159, 122, 234, 0.08)'}
                              boxShadow="sm"
                            >
                              <Flex justify="space-between" align="center" mb={2}>
                                <HStack spacing={2}>
                                  <Icon as={MdShield} color={isCritical ? 'red.400' : 'purple.400'} boxSize={4} />
                                  <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.05em">
                                    FlowSense AI Inundation Prediction
                                  </Text>
                                </HStack>
                                <Badge
                                  colorScheme={isCritical ? 'red' : isHigh ? 'orange' : 'purple'}
                                  variant="solid"
                                  fontSize="2xs"
                                  borderRadius="full"
                                  px={2}
                                >
                                  {sev}
                                </Badge>
                              </Flex>

                              <SimpleGrid columns={3} spacing={2} my={2}>
                                <Box p={2} bg={glassCardBg} borderRadius="md" textAlign="center">
                                  <Text fontSize="2xs" color={subtextColor} fontWeight="600">Overflow Probability</Text>
                                  <Text fontSize="md" fontWeight="900" color={isCritical ? 'red.500' : 'purple.400'}>
                                    {Math.round(prob * 100)}%
                                  </Text>
                                </Box>
                                <Box p={2} bg={glassCardBg} borderRadius="md" textAlign="center">
                                  <Text fontSize="2xs" color={subtextColor} fontWeight="600">Est. Time to Flood</Text>
                                  <Text fontSize="sm" fontWeight="800">
                                    {estMins !== null && estMins !== undefined
                                      ? (estMins === 0 ? 'IMMINENT' : `~${estMins} min`)
                                      : 'N/A'}
                                  </Text>
                                </Box>
                                <Box p={2} bg={glassCardBg} borderRadius="md" textAlign="center">
                                  <Text fontSize="2xs" color={subtextColor} fontWeight="600">Asset Health Score</Text>
                                  <Text fontSize="sm" fontWeight="800" color="teal.400">
                                    {aiInfo.health_score || 85}%
                                  </Text>
                                </Box>
                              </SimpleGrid>

                              <Text fontSize="2xs" color={subtextColor} mt={1}>
                                *Calculation integrates well silt clearance capacity ({latestScore}%), real-time rise velocity, and Open-Meteo rainfall saturation metrics.
                              </Text>
                              {/* --- UPDATE: live rainfall context + drill-down */}
                              <Flex justify="space-between" align="center" mt={2} gap={2} flexWrap="wrap">
                                {siteWeather ? (
                                  <HStack spacing={1} fontSize="2xs" color="blue.400">
                                    <Icon as={MdGrain} boxSize={3} />
                                    <Text fontWeight="700">
                                      {siteWeather.next6hRainMm} mm (6h) · {siteWeather.next24hRainMm} mm (24h) forecast
                                    </Text>
                                  </HStack>
                                ) : (
                                  <Box />
                                )}
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="purple"
                                  rightIcon={<MdAutoAwesome />}
                                  onClick={() => setDrawerTabIndex(2)}
                                >
                                  Full AI analysis
                                </Button>
                              </Flex>
                              {/* --- END UPDATE */}
                            </Box>
                          );
                        })()}

                        <SimpleGrid columns={2} spacing={3}>
                          <Box p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
                            <Text fontSize="xs" color={subtextColor}>Current Depth</Text>
                            <Text fontSize="sm" fontWeight="bold">{currentDepth}</Text>
                          </Box>
                          <Box p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
                            <Text fontSize="xs" color={subtextColor}>Base / Design Depth</Text>
                            <Text fontSize="sm" fontWeight="bold">{baseDepth}</Text>
                          </Box>
                          <Box p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
                            <Text fontSize="xs" color={subtextColor}>Casing Health</Text>
                            <Badge colorScheme={meta.casing_condition === 'critical' ? 'red' : meta.casing_condition === 'degraded' ? 'orange' : 'green'} mt={1}>
                              {(meta.casing_condition || 'Good').toUpperCase()}
                            </Badge>
                          </Box>
                          <Box p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
                            <Text fontSize="xs" color={subtextColor}>Last Cleaned Date</Text>
                            <Text fontSize="sm" fontWeight="bold">{meta.last_cleaned_date || 'N/A'}</Text>
                          </Box>
                        </SimpleGrid>

                        {meta.work_photo_url && (
                          <Box mt={2}>
                            <Text fontSize="xs" color={subtextColor} mb={1}>Latest Verified Site Photo:</Text>
                            <Image
                              src={resolvePhotoUrl(meta.work_photo_url)}
                              alt="Latest Inspection"
                              maxH="180px"
                              w="100%"
                              objectFit="cover"
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={cardBorderColor}
                              cursor="pointer"
                              fallback={
                                <Box p={4} textAlign="center" bg="gray.100" borderRadius="md">
                                  <Text fontSize="xs" color="gray.500">Image preview unavailable</Text>
                                </Box>
                              }
                              onClick={() => handleTriggerPhotoZoom(meta.work_photo_url, selectedWell.name || selectedWell.id)}
                            />
                          </Box>
                        )}
                      </VStack>
                    </TabPanel>

                    <TabPanel p={0}>
                      {history.length === 0 ? (
                        <Box py={8} textAlign="center">
                          <Text fontSize="xs" color={subtextColor}>No historical inspections recorded yet.</Text>
                        </Box>
                      ) : (
                        <VStack spacing={3} align="stretch">
                          {history.map((item, hIdx) => {
                            const score = item.clearance_score ?? 100;
                            const photo = resolvePhotoUrl(item.work_photo_url);

                            return (
                              <Box
                                key={item.id || hIdx}
                                p={3}
                                borderRadius="xl"
                                borderWidth="1px"
                                borderColor={cardBorderColor}
                                bg={hIdx === 0 ? selectedRowBg : statBoxBg}
                                position="relative"
                              >
                                {hIdx === 0 && (
                                  <Badge position="absolute" top={2} right={2} colorScheme="teal" fontSize="9px">
                                    LATEST
                                  </Badge>
                                )}

                                <Flex justify="space-between" align="start">
                                  <Box>
                                    <HStack spacing={1}>
                                      <Icon as={MdCalendarToday} boxSize={3} color="teal.500" />
                                      <Text fontSize="xs" fontWeight="800">
                                        {item.inspection_date ? new Date(item.inspection_date).toLocaleDateString() : 'N/A'}
                                      </Text>
                                    </HStack>
                                    <Text fontSize="xs" color={subtextColor}>
                                      By: <b>{item.inspector_name || 'Technician'}</b> ({item.inspector_role || 'Tech'})
                                    </Text>
                                  </Box>
                                  <Badge colorScheme={score < 80 ? 'red' : 'green'} fontSize="xs" px={2} py={0.5} borderRadius="md">
                                    Score: {score}%
                                  </Badge>
                                </Flex>

                                <Divider my={2} />

                                <SimpleGrid columns={3} spacing={2} fontSize="xs">
                                  <Box>
                                    <Text color={subtextColor}>Depth</Text>
                                    <Text fontWeight="bold">{item.measured_depth_m !== null ? `${item.measured_depth_m}m` : 'N/A'}</Text>
                                  </Box>
                                  <Box>
                                    <Text color={subtextColor}>Base</Text>
                                    <Text fontWeight="bold">{item.base_depth_m ? `${item.base_depth_m}m` : 'N/A'}</Text>
                                  </Box>
                                  <Box>
                                    <Text color={subtextColor}>Variance</Text>
                                    <Text fontWeight="bold" color={item.depth_variance_m < 0 ? 'orange.500' : 'green.600'}>
                                      {item.depth_variance_m > 0 ? `+${item.depth_variance_m}m` : `${item.depth_variance_m}m`}
                                    </Text>
                                  </Box>
                                </SimpleGrid>

                                {item.notes && (
                                  <Text fontSize="xs" mt={2} fontStyle="italic" color={subtextColor}>
                                    "{item.notes}"
                                  </Text>
                                )}

                                {photo && (
                                  <HStack mt={2} spacing={2} align="center">
                                    <Image
                                      src={photo}
                                      boxSize="38px"
                                      objectFit="cover"
                                      borderRadius="md"
                                      cursor="pointer"
                                      fallback={
                                        <IconButton
                                          size="xs"
                                          colorScheme="teal"
                                          variant="outline"
                                          icon={<MdBrokenImage />}
                                          aria-label="Image missing"
                                        />
                                      }
                                      onClick={() => handleTriggerPhotoZoom(photo, `${selectedWell.name || selectedWell.id} - ${item.inspection_date}`)}
                                    />
                                    <Text
                                      fontSize="10px"
                                      color="teal.500"
                                      fontWeight="600"
                                      cursor="pointer"
                                      onClick={() => handleTriggerPhotoZoom(photo, `${selectedWell.name || selectedWell.id} - ${item.inspection_date}`)}
                                    >
                                      View Attached Photo
                                    </Text>
                                  </HStack>
                                )}
                              </Box>
                            );
                          })}
                        </VStack>
                      )}
                    </TabPanel>

                    {/* --- UPDATE: AI & weather insights tab */}
                    <TabPanel p={0}>
                      <WellAiInsightsPanel
                        well={selectedWell}
                        ai={getAiForWell(selectedWell, aiPredictions)}
                        clearanceScore={latestScore}
                        siteWeather={siteWeather}
                        weatherLoading={weather ? false : siteWeatherState.isLoading || fleetWeatherState.isLoading}
                        weatherError={weather ? null : siteWeatherState.error || fleetWeatherState.error}
                        onReloadWeather={selectedWellCoords ? siteWeatherState.reload : fleetWeatherState.reload}
                        onCopySummary={handleCopyAiSummary}
                        subtextColor={subtextColor}
                        cardBorderColor={cardBorderColor}
                        glassCardBg={glassCardBg}
                        emptyBarColor={emptyBarColor}
                        staleAfterMs={staleAfterMs}
                      />
                    </TabPanel>
                    {/* --- END UPDATE */}
                    {/* --- UPDATE (wells v1.3): kind, intel, ground elevation, catch basins / drainage wells with inspections */}
                    <TabPanel p={0}>
                      <WellDrainageNetworkTab
                        well={selectedWell}
                        intelWell={intelById?.get?.(String(selectedWell.id)) || null}
                        onOpenExplainer={onOpenExplainer}
                        onCreateWorkOrder={onCreateWorkOrder}
                      />
                    </TabPanel>
                    {/* --- END UPDATE */}
                  </TabPanels>
                </Tabs>
              );
            })()}
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px" borderColor={cardBorderColor}>
            <Button
              variant="outline"
              size="sm"
              mr={3}
              onClick={() => setIsDetailsDrawerOpen(false)}
            >
              Close
            </Button>
            <Button
              colorScheme="teal"
              size="sm"
              leftIcon={<MdAssignmentTurnedIn />}
              onClick={() => {
                setIsDetailsDrawerOpen(false);
                handleOpenInspectionModal(selectedWell);
              }}
            >
              Update Inspection
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* ═══ INSPECTION MODAL ═══ */}
      <Modal
        isOpen={isInspectionModalOpen}
        onClose={() => setIsInspectionModalOpen(false)}
        size="lg"
      >
        <ModalOverlay backdropFilter="blur(6px)" />
        <ModalContent
          bg={modalBg}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={cardBorderColor}
        >
          <ModalHeader borderBottomWidth="1px" borderColor={cardBorderColor}>
            <HStack spacing={2}>
              <Icon as={MdShield} color="teal.400" />
              <Text fontSize="md">
                Record Maintenance: {selectedWell?.name || selectedWell?.id}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5}>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="xs" fontWeight="700">
                    Inspector / Contractor
                  </FormLabel>
                  <Input
                    size="sm"
                    borderRadius="lg"
                    value={inspectionForm.inspectorName}
                    onChange={(e) =>
                      setInspectionForm({ ...inspectionForm, inspectorName: e.target.value })
                    }
                    placeholder="e.g., John Smith"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="xs" fontWeight="700">
                    Role
                  </FormLabel>
                  <Select
                    size="sm"
                    borderRadius="lg"
                    value={inspectionForm.inspectorRole}
                    onChange={(e) =>
                      setInspectionForm({ ...inspectionForm, inspectorRole: e.target.value })
                    }
                  >
                    <option value="technician">Internal Technician</option>
                    <option value="contractor">Drilling Contractor</option>
                    <option value="inspector">Government Safety Inspector</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="xs" fontWeight="700">
                    Inspection Date
                  </FormLabel>
                  <Input
                    size="sm"
                    type="date"
                    borderRadius="lg"
                    value={inspectionForm.inspectionDate}
                    onChange={(e) =>
                      setInspectionForm({ ...inspectionForm, inspectionDate: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="700">
                    Date Last Cleaned / Flushed
                  </FormLabel>
                  <Input
                    size="sm"
                    type="date"
                    borderRadius="lg"
                    value={inspectionForm.lastCleanDate}
                    onChange={(e) =>
                      setInspectionForm({ ...inspectionForm, lastCleanDate: e.target.value })
                    }
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="700">
                    Base / Design Depth (Meters)
                  </FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    step="0.1"
                    borderRadius="lg"
                    value={inspectionForm.baseDepthM}
                    onChange={(e) =>
                      setInspectionForm({ ...inspectionForm, baseDepthM: e.target.value })
                    }
                    placeholder="e.g., 145.0"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="700">
                    Current Measured Depth (Meters)
                  </FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    step="0.1"
                    borderRadius="lg"
                    value={inspectionForm.wellDepthM}
                    onChange={(e) =>
                      setInspectionForm({ ...inspectionForm, wellDepthM: e.target.value })
                    }
                    placeholder="e.g., 125.0"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="700">
                  Casing Condition
                </FormLabel>
                <Select
                  size="sm"
                  borderRadius="lg"
                  value={inspectionForm.casingCondition}
                  onChange={(e) =>
                    setInspectionForm({ ...inspectionForm, casingCondition: e.target.value })
                  }
                >
                  <option value="good">Good / Structurally Sound</option>
                  <option value="degraded">Degraded / Mild Corrosion & Scaling</option>
                  <option value="critical">Critical / Compromised Casing</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="700">
                  Operational Status
                </FormLabel>
                <Select
                  size="sm"
                  borderRadius="lg"
                  value={inspectionForm.operationalStatus}
                  onChange={(e) =>
                    setInspectionForm({ ...inspectionForm, operationalStatus: e.target.value })
                  }
                >
                  <option value="operational">Operational</option>
                  <option value="maintenance_required">Maintenance Required</option>
                  <option value="damaged">Damaged / Offline</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="700">
                  Work Evidence Photo URL / Path
                </FormLabel>
                <Input
                  size="sm"
                  borderRadius="lg"
                  value={inspectionForm.photoUrl}
                  onChange={(e) =>
                    setInspectionForm({ ...inspectionForm, photoUrl: e.target.value })
                  }
                  placeholder="https://... image link or /uploads/..."
                />
              </FormControl>

              {inspectionForm.photoUrl && (
                <Box mt={1}>
                  <Text fontSize="2xs" color={subtextColor} mb={1}>
                    Photo Preview:
                  </Text>
                  <Image
                    src={resolvePhotoUrl(inspectionForm.photoUrl)}
                    alt="Work Verification Preview"
                    maxH="150px"
                    w="100%"
                    objectFit="cover"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={cardBorderColor}
                    fallback={
                      <Box p={3} bg="gray.100" borderRadius="md" textAlign="center">
                        <Text fontSize="xs" color="gray.500">
                          Preview Unavailable
                        </Text>
                      </Box>
                    }
                  />
                </Box>
              )}

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="700">
                  Inspection Summary Notes
                </FormLabel>
                <Textarea
                  size="sm"
                  borderRadius="lg"
                  rows={3}
                  value={inspectionForm.notes}
                  onChange={(e) =>
                    setInspectionForm({ ...inspectionForm, notes: e.target.value })
                  }
                  placeholder="Describe wellhead clearance, pump status, sediment buildup..."
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter borderTopWidth="1px" borderColor={cardBorderColor}>
            <Button
              variant="outline"
              size="sm"
              mr={3}
              onClick={() => setIsInspectionModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              size="sm"
              isLoading={isSaving}
              onClick={handleSaveInspection}
            >
              Save Record
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ═══ ADMIN BASE DEPTH CALIBRATION MODAL ═══ */}
      <Modal isOpen={isCalibrateModalOpen} onClose={() => setIsCalibrateModalOpen(false)} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent bg={modalBg} borderRadius="2xl" borderWidth="1px" borderColor={cardBorderColor}>
          <ModalHeader borderBottomWidth="1px" borderColor={cardBorderColor}>
            <HStack spacing={2}>
              <Icon as={MdBuild} color="purple.400" />
              <Text fontSize="md">Calibrate Benchmark: {selectedWell?.name || selectedWell?.id}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="xs" color={subtextColor}>
                Set the permanent baseline design depth (base_depth_m). All future inspection measurements will compute clearance scores and silt accumulation variance against this benchmark.
              </Text>
              <FormControl isRequired>
                <FormLabel fontSize="xs" fontWeight="700">Baseline Design Depth (Meters)</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  step="0.1"
                  borderRadius="lg"
                  value={calibrationBaseDepth}
                  onChange={(e) => setCalibrationBaseDepth(e.target.value)}
                  placeholder="e.g., 145.0"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor={cardBorderColor}>
            <Button variant="ghost" size="sm" mr={3} onClick={() => setIsCalibrateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              size="sm"
              isLoading={isCalibrating}
              onClick={handleSaveCalibration}
            >
              Save Benchmark
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ═══ PHOTO LIGHTBOX PREVIEW MODAL ═══ */}
      <Modal
        isOpen={isPhotoPreviewOpen}
        onClose={() => setIsPhotoPreviewOpen(false)}
        size="3xl"
        isCentered
      >
        <ModalOverlay backdropFilter="blur(8px)" />
        <ModalContent bg={modalBg} borderRadius="2xl" overflow="hidden">
          <ModalHeader fontSize="sm">
            <HStack spacing={2}>
              <Icon as={MdPhotoCamera} color="teal.400" />
              <Text>{previewWellTitle}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={4} display="flex" justifyContent="center" alignItems="center">
            <Image
              src={previewPhotoUrl}
              alt="Verification Zoom"
              maxH="75vh"
              w="100%"
              objectFit="contain"
              borderRadius="lg"
              fallback={
                <VStack p={10} spacing={3}>
                  <Icon as={MdBrokenImage} boxSize={12} color="gray.400" />
                  <Text fontSize="sm" color="gray.500">
                    Unable to load photo asset. Check file permissions or path.
                  </Text>
                </VStack>
              }
            />
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor={cardBorderColor}>
            <Button size="sm" onClick={() => setIsPhotoPreviewOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default memo(WellManagementPanel);